import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  CHARGE_RATES_QUERY_KEY,
  CHARGE_RATES_UPDATE_QUERY_KEY,
  CHARGE_RATES_URL
} from './queryConstants'

export type ChargeRateDto = {
  _id?: string
  fundingTypeId: string
  eventTypeId: string
  visitTypeName?: string | null
  rate: number
  rateType: 'HOURLY' | 'EVENT'
}

export type ChargeRateFormEntry = {
  visitTypeId?: string
  visitTypeName: string
  amount: number
  unit: 'per_hour' | 'fixed'
}

export type UpdateChargeRatesDto = {
  fundingSources: Array<{
    type: string
    chargeRates: ChargeRateFormEntry[]
  }>
}

export const useGetChargeRates = (residentId: string | undefined) => {
  return useQuery<ChargeRateDto[]>(
    [CHARGE_RATES_QUERY_KEY, residentId],
    async () =>
      getJSON({
        url: `${CHARGE_RATES_URL}/${residentId}`,
        isRota: true
      }),
    { enabled: !!residentId, staleTime: 30_000, refetchOnWindowFocus: false }
  )
}

export const useUpdateChargeRates = (residentId: string | undefined) => {
  const queryClient = useQueryClient()

  const updateFunction = (dto: UpdateChargeRatesDto): Promise<void> =>
    postJSON({
      url: `${CHARGE_RATES_URL}/${residentId}`,
      method: 'PUT',
      json: dto,
      isRota: true,
      responseType: 'text'
    })

  const { mutateAsync: updateChargeRates, isLoading: isUpdatingChargeRates } =
    useMutation<void, unknown, UpdateChargeRatesDto, unknown>(
      [CHARGE_RATES_UPDATE_QUERY_KEY, residentId],
      updateFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries([CHARGE_RATES_QUERY_KEY, residentId])
        },
        onError: (err) => {
          const someError = err as any
          const title =
            typeof err === 'string'
              ? err
              : (typeof someError?.message === 'string' && someError.message) ||
                (typeof someError?.msg === 'string' && someError.msg) ||
                (typeof someError?.error === 'string' && someError.error) ||
                'Something went wrong'
          showToast({ title, type: 'error' })
        }
      }
    )

  return { updateChargeRates, isUpdatingChargeRates }
}
